import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { APIService, ContentType, CreateModuleInput, ModuleType, PageType, QuestionType } from '../../API.service';
import { FeedbackType } from './feedback-type';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ContentSelectDialogComponent } from '../content-select-dialog/content-select-dialog.component';
import { ContentGridElement } from '../content-grid/content-grid-element';
import { ContentGridSelectMode } from '../content-grid/content-grid-select-mode';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthenticatorService } from '@aws-amplify/ui-angular';

@Component({
  selector: 'app-module-form',
  templateUrl: './module-form.component.html',
  styleUrls: ['./module-form.component.scss']
})
export class ModuleFormComponent implements OnInit {

  loading: boolean;
  moduleForm: FormGroup;

  institutionId: string;
  navigationNodeId: string;
  moduleId: string;
  moduleImageFile: File;

  DEFAULT_MODULE_TYPE: ModuleType = ModuleType.SLIDESHOW;
  DEFAULT_QUESTION_TYPE: QuestionType = QuestionType.MULTIPLE_CHOICE;

  questionTypeChangeSubs : Map<number, Subscription> = new Map<number, Subscription>();

  ContentType = ContentType;
  FeedbackType = FeedbackType;
  ModuleType = ModuleType;
  QuestionType = QuestionType;
  PageType = PageType;

  QuestionTypesWithChoices = [QuestionType.MULTIPLE_CHOICE, QuestionType.MULTIPLE_SELECT];
  MinScaleLength = 3
  MaxScaleLength = 10
  ScaleLengths = [...Array((this.MaxScaleLength + 1) - this.MinScaleLength).keys()].map(i => i + this.MinScaleLength)

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: APIService,
    private formBuilder: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private authenticator: AuthenticatorService) { }

  ngOnInit(): void {

    this.loading = true;

    var routeSub = this.activatedRoute.params.subscribe(async params => {

      await this.init(
        params["moduleId"] ?? null,
        params["institutionId"] ?? null,
        params["nodeId"] ?? null
      );

      routeSub.unsubscribe();

    });

  }

  addSlides() {
    this.dialog.open<ContentSelectDialogComponent, any, Array<ContentGridElement>>(ContentSelectDialogComponent, {
      data: {
        title: "My Library",
        selectButtonText: "Add Slides",
        selectMode: ContentGridSelectMode.MULTIPLE
      }
    })
      .afterClosed()
      .subscribe(result => {
        
        if (!result) return;

        var pagesFormArray = this.moduleForm.controls.pages as FormArray;

        result.forEach((contentGridElement) => {

          var slideFormGroup = this.newSlideFormGroup(contentGridElement, null);

          var index = pagesFormArray.length;

          contentGridElement.deleted.subscribe(() => {
            pagesFormArray.removeAt(index);
          })

          var pageFormGroup = this.newPageFormGroup(PageType.SLIDE, null, slideFormGroup)

          pagesFormArray.push(pageFormGroup);

        })

      })
  }

  async cancel() {
    var commands = ["home"]
    if (this.navigationNodeId){
      commands.push(this.navigationNodeId)
    }
    this.router.navigate(commands)
  }

  async init(moduleId, institutionId, nodeId) {

    this.moduleId = moduleId;
    this.institutionId = institutionId;
    this.navigationNodeId = nodeId;

    this.moduleForm = this.formBuilder.group({
      label: this.formBuilder.control('', [Validators.required]),
      description: this.formBuilder.control(''),
      visible: this.formBuilder.control(true, [Validators.required]),
      imageSrc: this.formBuilder.control(''),
      imageKey: this.formBuilder.control(''),
      type: this.formBuilder.control(this.DEFAULT_MODULE_TYPE, [Validators.required]),
      pages: this.formBuilder.array([], [Validators.required]),
      videos: this.formBuilder.array([])
    })

    await this.loadModule();

    this.loading = false;

  }

  async loadModule() {

    if (!this.moduleId) return;

    var module = await this.api.getModuleForAdmin(this.moduleId);

    if (!module.navigationNode.id || module.locked || !module.active) this.cancel();

    this.institutionId = module.institution.id
    this.navigationNodeId = module.navigationNode.id

    this.moduleForm.controls.label.setValue(module.label);
    this.moduleForm.controls.description.setValue(module.description);
    this.moduleForm.controls.type.setValue(module.type);
    this.moduleForm.controls.visible.setValue(module.visible);

    var pagesFormArray = (this.moduleForm.controls.pages as FormArray)

    module.pages.items.sort((a, b) => a.order - b.order).forEach((page, pageIndex) => {

      if (page.type == PageType.SLIDE){

        var content = page.slide.contents.items[0].content;

        var c = null;

        if (content) {
          c = new ContentGridElement(content.type, this.sanitizer, content.id, content.id, content.value);
        }
        
        var g = this.newSlideFormGroup(c, page.slide.notes);

        var p = this.newPageFormGroup(page.type, null, g);

        pagesFormArray.push(p);

      }

    })

  }

  newPageFormGroup(type: PageType, questionFormGroup?: FormGroup, slideFormGroup?: FormGroup): FormGroup {

    return this.formBuilder.group({
      type: this.formBuilder.control(type, [Validators.required]),
      question: questionFormGroup,
      slide: slideFormGroup
    })
    
  }

  newSlideFormGroup(content: ContentGridElement, notes: string){
    return this.formBuilder.group({
      content: this.formBuilder.control(content, [Validators.required]),
      notes: this.formBuilder.control(notes)
    });
  }

  onModuleTypeChange() {
    var moduleType = this.moduleForm.controls.type.value;
    var pagesFormArray = (this.moduleForm.controls.pages as FormArray)
    if (moduleType == ModuleType.SLIDESHOW){
      for(var pageIndex = pagesFormArray.controls.length; pageIndex >= 0; pageIndex--){
        this.removePage(pageIndex);
      }
    }
  }

  removePage(pageIndex: number) : void {
    var pages = this.moduleForm.controls.pages as FormArray;
    pages.removeAt(pageIndex);
  }

  async submit() {

    if (this.moduleForm.invalid) {
      console.log(this.moduleForm);
      return;
    }

    this.loading = true;

    var order = 0;

    if (this.moduleId) {

      var m = await this.api.GetModule(this.moduleId)
      
      if (m.locked || !m.active) {

        alert("This module has been edited since you opened this page. Your changes will NOT be applied.")
        this.cancel();
        return;

      } else {

        await this.api.UpdateModule({ id: this.moduleId, active: false })

      }

      order = m.order;

    } else {

      var modules = await this.api.ListModules({
        navigationNodeID: {
          eq: this.navigationNodeId
        },
        active: {
          eq: true
        }
      })

      var order = modules.items.length;

    }
    
    var module : CreateModuleInput = {
      active: false,
      image: "",
      type: this.moduleForm.value.type,
      moduleInstitutionId: this.institutionId,
      navigationNodeID: this.navigationNodeId,
      label: this.moduleForm.value.label,
      order: order,
      visible: this.moduleForm.value.visible,
      locked: true,
      ownerID: this.authenticator.user.attributes.sub
    }

    await this.api.CreateModule(module).then(this.OnCreateModule.bind(this))
    this.cancel();
    
  }
  
  async OnCreateModule(newModule) {

    this.moduleId = newModule;

    var promises = [];

    //CREATE PAGES
    this.moduleForm.value.pages.forEach((pageToCreate, i) => {

      var createPagePromise = this.api.CreatePage({
        pageModuleId: newModule.id,
        order: i,
        type: pageToCreate.type
      }).then((newPage) => {
        if (newPage.type == PageType.SLIDE){
          this.api.CreateSlide({
            pageID: newPage.id,
            notes: pageToCreate.slide.notes
          }).then(newSlide => {
            this.api.CreateSlideContent({
              slideContentContentId: pageToCreate.slide.content.contentId,
              slideContentSlideId: newSlide.pageID,
              order: 0
            })
          })
        }
      })

      promises.push(createPagePromise);

    })

    await Promise.all(promises)

    await this.api.UpdateModule({id: newModule.id, active: true, locked: false})

  }

}
